import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";
import { ComparisonTable } from "@/components/ComparisonTable";
import { ProductCard } from "@/components/ProductCard";
import { ArrowRight, Scales } from "@phosphor-icons/react";

export function CompareIndexPage() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/comparisons").then((r) => setItems(r.data)); }, []);
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 sm:py-16">
      <SEO title="Compare products side by side" description="Side-by-side comparisons of the best home & kitchen products on Amazon.in — price, ratings, key specs and who each one is best for." path="/compare" />
      <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-3">Compare</p>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C2B29]">Side-by-side comparisons.</h1>
      <p className="text-base text-[#5C5A56] mt-3 max-w-2xl">Can't decide? See prices, ratings, specs and our verdict in one table.</p>
      <div className="grid sm:grid-cols-2 gap-6 mt-10">
        {(items || []).map((c) => (
          <Link key={c.id} to={`/compare/${c.slug}`} data-testid={`compare-card-${c.slug}`} className="hh-card rounded-2xl p-6 group">
            <div className="flex -space-x-3 mb-5">
              {c.thumbs.map((t) => <img key={t} src={t} alt="" className="w-16 h-16 rounded-full object-cover border-4 border-white" />)}
            </div>
            <span className="text-xs uppercase tracking-wider text-[#5B7B53]">{c.category} · {c.product_ids.length} products</span>
            <h2 className="font-display text-2xl text-[#2C2B29] mt-1 group-hover:text-[#5B7B53] transition-colors">{c.title}</h2>
            <p className="text-sm text-[#5C5A56] mt-2 line-clamp-2">{c.intro}</p>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#D17A58] mt-4">Compare now <ArrowRight size={14} weight="bold" /></span>
          </Link>
        ))}
      </div>
      {items && items.length === 0 && <p className="text-[#5C5A56] mt-10">No comparisons yet.</p>}
    </div>
  );
}

export function CompareDetailPage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => { setC(null); api.get(`/comparisons/${slug}`).then((r) => setC(r.data)).catch(() => setC(false)); }, [slug]);
  if (c === false) return <div className="max-w-3xl mx-auto p-10 text-[#5C5A56]">Comparison not found.</div>;
  if (!c) return <div className="max-w-3xl mx-auto p-10 text-[#5C5A56]">Loading...</div>;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 sm:py-16">
      <SEO title={c.title} description={c.intro?.slice(0, 155)} image={c.products[0]?.image_url} path={`/compare/${c.slug}`} />
      <nav className="text-xs text-[#5C5A56] mb-6"><Link to="/compare" className="hover:text-[#5B7B53]">Compare</Link><span className="mx-2">/</span>{c.title}</nav>
      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5B7B53]"><Scales size={16} /> {c.category}</span>
      <h1 data-testid="compare-title" className="font-display text-4xl sm:text-5xl text-[#2C2B29] mt-2">{c.title}</h1>
      <p className="text-base sm:text-lg text-[#5C5A56] mt-3 max-w-3xl">{c.intro}</p>
      <ComparisonTable products={c.products} labels={c.labels} title="" testid="compare-page-table" />
      <h2 className="font-display text-2xl text-[#2C2B29] mb-6">Take a closer look</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {c.products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </div>
  );
}
